import React from "react";
import { Card, Checkbox, Col, Form, Input, Row } from "antd";
import HotelTicketRoomPan from "./HotelTicketRoomPan";
import {
  getHotelReviewPrimaryPanRoomIndex,
  getHotelReviewRoomPanDisplay,
  groupHotelTicketGuestsByRoom,
  guestHasPan,
  isHotelAdultGuest,
} from "./hotelhelper";

const HotelTicketVoucherPanSection = ({
  ticketData = {},
  samePanForAllRooms = false,
  verifiedPanRoomIndex = null,
  onSamePanChange = () => {},
  onPanVerified = () => {},
  panFieldErrors = {},
  onPanFieldTouched = () => {},
  hideRoomGuestForms = false,
}) => {
  const rooms = groupHotelTicketGuestsByRoom(ticketData);
  const referenceNo = ticketData?.RefNumber ?? "";
  const primaryPanRoomIndex =
    samePanForAllRooms && Number.isFinite(verifiedPanRoomIndex)
      ? verifiedPanRoomIndex
      : getHotelReviewPrimaryPanRoomIndex(rooms);

  const getGuestTitle = (guest) =>
    String(guest?.title ?? guest?.Title ?? "").trim();

  const getGuestFirstName = (guest) =>
    String(guest?.firstName ?? guest?.FirstName ?? "").trim();

  const getGuestLastName = (guest) =>
    String(guest?.lastName ?? guest?.LastName ?? "").trim();

  const getGuestPanValue = (guest) =>
    String(guest?.pan ?? guest?.Pan ?? "").trim().toUpperCase();

  const getPanHolderGuestIndex = (room) => {
    const list = room?.paxInfoList ?? [];
    const withPan = list.findIndex((pax) => guestHasPan(pax));
    if (withPan >= 0) {
      return withPan;
    }
    const leadAdult = list.findIndex((pax) => isHotelAdultGuest(pax));
    return leadAdult >= 0 ? leadAdult : 0;
  };

  const shouldShowPanFieldForGuest = (roomIndex, guestIndex, guest, room) => {
    if (samePanForAllRooms && roomIndex !== primaryPanRoomIndex) {
      return false;
    }
    if (guestHasPan(guest)) {
      return guestIndex === getPanHolderGuestIndex(room);
    }
    if (!isHotelAdultGuest(guest)) {
      return false;
    }
    return guestIndex === getPanHolderGuestIndex(room);
  };

  const shouldShowPanBadge = (roomIndex) => {
    const pan = getHotelReviewRoomPanDisplay(rooms, roomIndex);
    if (!pan) {
      return false;
    }
    if (samePanForAllRooms) {
      return roomIndex === primaryPanRoomIndex;
    }
    return true;
  };

  const getLeadAdultInRoom = (room) =>
    (room?.paxInfoList ?? []).find((guest) => isHotelAdultGuest(guest)) ??
    room?.paxInfoList?.[0];

  return (
    <div className="hotel-ticket-voucher-pan-section">
      <div className="same-pan-checkbox-row">
        <Checkbox
          checked={samePanForAllRooms}
          onChange={(e) => onSamePanChange(e.target.checked)}
        >
          <strong>Same Pan Card For All Room</strong>
        </Checkbox>
      </div>

      {!hideRoomGuestForms && (
        <Form layout="vertical" className="hotel-ticket-guest-form">
          {rooms.map((room, roomIndex) => {
            const roomPan = getHotelReviewRoomPanDisplay(rooms, roomIndex);
            const leadAdult = getLeadAdultInRoom(room);

            return (
              <Card
                bordered={false}
                className="guest-details-form hotel-ticket-room-card"
                key={`ticket-room-pan-${roomIndex}`}
                id={`ticket-room-pan-${roomIndex}`}
              >
                <p className="room-title">
                  <span>Room {roomIndex + 1}</span>
                  {shouldShowPanBadge(roomIndex) ? (
                    <span className="room-pan-badge">{roomPan}</span>
                  ) : null}
                </p>

                {(room?.paxInfoList ?? []).map((guest, guestIndex) => {
                  const guestKey = `${roomIndex}_${guestIndex}`;
                  const isAdult = isHotelAdultGuest(guest);
                  const showPanField = shouldShowPanFieldForGuest(
                    roomIndex,
                    guestIndex,
                    guest,
                    room,
                  );
                  const guestPan = getGuestPanValue(guest);
                  const panHolder = (room?.paxInfoList ?? []).find((pax) =>
                    guestHasPan(pax),
                  );

                  return (
                    <div
                      key={guestKey}
                      className="guest-input-wrapper"
                      id={`ticket-guest-${guestKey}`}
                    >
                      <p className="guestsType">{isAdult ? "Adult" : "Child"}</p>
                      <Row gutter={[24, 16]} align="bottom">
                        <Col
                          md={3}
                          sm={8}
                          xs={12}
                          className="hotel-review-title-col"
                        >
                          <Form.Item label="Title" required>
                            <Input
                              className="hotel-review-readonly-input"
                              value={getGuestTitle(guest)}
                              readOnly
                            />
                          </Form.Item>
                        </Col>
                        <Col md={6} sm={8} xs={12}>
                          <Form.Item label="First Name" required>
                            <Input
                              className="hotel-review-readonly-input"
                              value={getGuestFirstName(guest)}
                              readOnly
                            />
                          </Form.Item>
                        </Col>
                        <Col md={6} sm={8} xs={12}>
                          <Form.Item label="Last Name" required>
                            <Input
                              className="hotel-review-readonly-input"
                              value={getGuestLastName(guest)}
                              readOnly
                            />
                          </Form.Item>
                        </Col>
                        {showPanField && !guestPan ? (
                          <Col md={9} sm={24} xs={24}>
                            <HotelTicketRoomPan
                              referenceNo={referenceNo}
                              guestFirstName={
                                getGuestFirstName(panHolder ?? leadAdult)
                              }
                              guestLastName={
                                getGuestLastName(panHolder ?? leadAdult)
                              }
                              appliedPan={roomPan}
                              fieldError={panFieldErrors[String(roomIndex)] ?? ""}
                              onPanInputChange={() => onPanFieldTouched(roomIndex)}
                              onVerified={(panNumber, panMeta) =>
                                onPanVerified(panNumber, panMeta, roomIndex)
                              }
                            />
                          </Col>
                        ) : showPanField && guestPan ? (
                          <Col md={9} sm={24} xs={24}>
                            <Form.Item label="Pan Number">
                              <Input
                                className="hotel-review-readonly-input hotel-review-pan-display-input"
                                value={guestPan}
                                readOnly
                              />
                            </Form.Item>
                          </Col>
                        ) : null}
                      </Row>
                    </div>
                  );
                })}
              </Card>
            );
          })}
        </Form>
      )}
    </div>
  );
};

export default HotelTicketVoucherPanSection;
